﻿import { ObjectUtils } from '../main/ObjectUtils.js';
import { ProductDrawingViewModel } from './ProductDrawingViewModel.js';

/**
 * The MultiStepProductEditor supports two different screen sizes. This Screen class represents
 * properties and methods specific to these screens. */
class MspScreen {
  constructor(config, editor) {
    this.slides = config.slides;
    this.editScopeBtnGroup = config.editScopeBtnGroup;
    this.componentTypeBtnGroup = config.componentTypeBtnGroup;
    this.gallery = config.gallery;
    this.editor = editor;
    this.elements = config.elements;
    this.UIContent = config.content;
    this.defaultSlideIndex = 0;
    this.$galleryFilterMenu = config.$galleryFilterMenu;

    if (editor.popupEditorConfig.showEditScopeStep) {
      this.editScopeButtons = this.editScopeBtnGroup.querySelectorAll('.btn');
      this.addEditScopeClickHandler();
    }
    if (editor.popupEditorConfig.showCompTypeStep) {
      this.componentTypeButtons = this.componentTypeBtnGroup.querySelectorAll('.btn');
      this.addComponentTypeClickHandler();
    }
    this.addGalleryItemClickHandler();
  }

  /** Some event handlers get auto-uninstalled when we manipulate the owl carousel. Hence, we must
   * ensure to uninstall/install them before/after the owl changes. */
  installEventHandlers() {
    this.addFilterMenuClickHandler();
  }

  /** Some event handlers get auto-uninstalled when we manipulate the owl carousel. Hence, we must
   * ensure to uninstall/install them before/after the owl changes. */
  uninstallEventHandlers() {
    this.removeFilterMenuClickHandler();
  }

  applyGalleryFilter() {
    const filterKey = this.editor.componentType.filterKey || 'All';
    const filterTitle = this.editor.componentType.filterTitle;
    this.galleryItems.forEach(item => {
      const isIncluded = filterKey === 'All' || item.dataset.appearanceKey === filterKey;
      item.parentElement.classList.toggle('d-none', !isIncluded);
    });
    // Only update the button content when title is provided:
    if (filterTitle) {
      this.filterBtn.textContent = filterTitle;
    }
  }

  updateInUseGalleryItems() {
    if (this.editor.componentType) {
      const product = this.editor.drawing.product;
      if (product) {
        const usedValues = this.editor.componentType.getAllUsedValues(product);
        this.setInUseValues(usedValues);
      }
    }
  }

  queryGalleryItems() {
    this.galleryItems = this.gallery.querySelectorAll('.gallery-item');
    this.updateInUseGalleryItems();
    this.applyGalleryFilter();
  }

  addEditScopeClickHandler() {
    this.editScopeBtnGroup.onclick = (event) => {
      const btn = event.target;
      const oldScope = this.editor.editScope;
      this.editor.setEditScope(btn.dataset.editScope);
      this.editor.setCurrentStep(EditorSteps.drawing);
      this.doOnEditScopeClicked(oldScope);
    };
  }

  addComponentTypeClickHandler() {
    this.componentTypeBtnGroup.onclick = (event) => {
      const btn = event.target;
      this.editor.setComponentTypeById(btn.dataset.compType);
    };
  }

  addGalleryItemClickHandler() {
    this.gallery.onclick = (event) => {
      if (event.target.classList && event.target.classList.contains('zoom-target')) {
        return;
      }
      // Don't allow clicks in the gallery until edit scope has been set. This applies to the set up
      // where edit scope is displayed before the gallery.
      //if (!this.editor.editScope) {
      //  return;
      //}
      const galleryItem = ObjectUtils.findAncestor(event.target, (el) => el.classList && el.classList.contains('gallery-item'));
      if (galleryItem) {
        this.editor.setSelectedValue(galleryItem.dataset.key);
        const nextStep = this.isEditScopeStepNeeded() ? EditorSteps.editScope : EditorSteps.drawing;
        this.editor.setCurrentStep(nextStep);
        this.doOnGalleryItemClicked();
      }
    };
  }

  removeFilterMenuClickHandler() {
    this.$galleryFilterMenu.off('hidden.bs.dropdown');
  }

  addFilterMenuClickHandler() {
    if (this.$galleryFilterMenu.length > 0) {
      this.filterBtn = this.$galleryFilterMenu[0].querySelector('button');
      this.$galleryFilterMenu.on('hidden.bs.dropdown', (event) => {
        // Note event.clickEvent is undefined on a secondary click on the dropdown button (to close it):
        if (event.clickEvent) {
          const menuItem = event.clickEvent.target;
          // Skip clicks outside the dropdown menu by testing for the dataset.key value:
          if (menuItem && menuItem.dataset.key) {
            this.editor.applyGalleryFilter(menuItem.dataset.key, menuItem.textContent);
          }
        }
      });
    }
  }

  doOnEditScopeClicked(oldScope) {
  }

  doOnGalleryItemClicked() {
  }

  componentTypeChanged(componentType) {
    if (this.componentTypeButtons) {
      this.componentTypeButtons.forEach(btn => {
        if (btn.dataset.editComp === componentType.id) {
          btn.classList.replace('btn-outline-primary', 'btn-primary');
        } else {
          btn.classList.replace('btn-primary', 'btn-outline-primary');
        }
      });
    }
    this.updateDrawingTitle();
  }

  updateDrawingTitle() {
    if (this.elements.drawingTitle) {
      let title = null;
      if (this.editor.componentType && this.editor.editScope) {
        const compType = this.UIContent[this.editor.componentType.id];
        title = compType.drawingTitle[this.editor.editScope];
        this.elements.drawingTitle.textContent = title;
      }
    }
  }

  editScopeChanged(editScope) {
    if (this.editScopeButtons) {
      // Mark the selected button:
      this.editScopeButtons.forEach(btn => {
        if (btn.dataset.editScope === editScope) {
          btn.classList.replace('btn-outline-primary', 'btn-primary');
        } else {
          btn.classList.replace('btn-primary', 'btn-outline-primary');
        }
      });
    }
    // Update other UI content
    this.updateDrawingTitle();
  }

  getSelectedGalleryItem() {
    return this.gallery.querySelector('.selected');
  }

  selectedValueChanged(value) {
    // We had a case (error report) of this.galleryItems not being defined. 4/3/2023.
    if (this.galleryItems) {
      this.galleryItems.forEach(item => {
        const isSelected = item.dataset.key === value;
        item.classList.toggle('selected', isSelected);
      });
    }
  }

  /**
   * Mark/unmark the gallery items as being in-use, i.e. whether a finish is used on one or more panels.
   */
  setInUseValues(values) {
    this.galleryItems.forEach(item => {
      const found = values.includes(item.dataset.key);
      item.classList.toggle('in-use', found);
    });
  }

  getCurrentSlide() {
    return this.editor.getCurrentSlide();
  }

  isSlideNeeded(slideElement) {
    /* All slides are needed except for the edit scope slide when component type is frame or track. */
    return slideElement !== this.editor.editScopeSlide || this.isEditScopeStepNeeded();
  }

  isEditScopeStepNeeded() {
    // For instance, edit scope is not needed if component type is frame or track:
    //return this.editor.componentType !== ComponentType.track && this.editor.componentType !== ComponentType.frame;
    return this.editor.componentType.useEditScope;
  }

  isGalleryVisible() {
    return this.getCurrentSlide() === this.editor.gallerySlide;
  }

  isDrawingSlideVisible() {
    return this.getCurrentSlide() === this.editor.drawingSlide;
  }

  scrollToActiveGalleryItem() {
    if (this.isGalleryVisible()) {
      const selectedGalleryItem = this.getSelectedGalleryItem();
      if (selectedGalleryItem /*&& !ObjectUtils.isScrolledIntoView(selectedGalleryItem, true)*/) {
        /* Calling the scroll function right away causes the next owl carousel slide to move into view,
         * maybe because we're just barely overlapping to the right of the slide? Anyway, delaying a
         * bit before scrolling solves the issue and doesn't seem to cause any problems. */
        setTimeout(() => selectedGalleryItem.scrollIntoView(), 250);
        //console.log('scrolling....');
        //selectedGalleryItem.scrollIntoView();
      }
    }
  }

  currentSlideChanged(oldIndex) {
    this.scrollToActiveGalleryItem();
  }

  stepActivated(step) {
    switch (step) {
      case EditorSteps.gallery:
        break;
      case EditorSteps.editScope:
        break;
      case EditorSteps.drawing:
        // The drawing title might be initially hidden. We'll display it now:
        this.elements.drawingTitle.classList.remove('d-none', 'invisible');
        break;
    }
  }
}

class MspLargeScreen extends MspScreen {
  constructor(config, editor) {
    super(config, editor);
  }

  isGalleryVisible() {
    return true;
  }

  isSlideNeeded(slideElement) {
    // The only slide we need is the drawing slide:
    return slideElement === this.editor.drawingSlide;
  }

  doOnEditScopeClicked(oldScope) {
    ///* For the large screen we only want to update the drawing the very first time the user clicks
    // * on the "front" edit scope button. After that the drawing is updated when the user clicks one
    // * of the gallery items. */
    //if (!oldScope && this.editor.editScope === EditScope.front) {
    //  // It's the first click on edit scope, and the "front" scope was selected -> auto update selected value
    //  this.editor.triggerDrawingClick();
    //}
  }

  doOnGalleryItemClicked() {
    //if (this.editor.editScope === EditScope.front) {
    //  this.editor.triggerDrawingClick();
    //}
  }

  stepActivated(step) {
    super.stepActivated(step);
    const elements = this.elements;
    switch (step) {
      case EditorSteps.gallery:
        if (this.editor.popupEditorConfig.showEditScopeStep) {
          elements.editScopeContainer.classList.remove('active');
          elements.editScopeTitle.classList.remove('active');
        }
        elements.drawingTitle.classList.remove('active');
        elements.galleryTitle.classList.add('active');
        break;
      case EditorSteps.editScope:
        elements.galleryTitle.classList.remove('active');
        elements.drawingTitle.classList.remove('active');
        if (this.editor.popupEditorConfig.showEditScopeStep) {
          elements.editScopeContainer.classList.add('active');
          elements.editScopeTitle.classList.add('active');
        }
        break;
      case EditorSteps.drawing:
        elements.galleryTitle.classList.remove('active');
        if (this.editor.popupEditorConfig.showEditScopeStep) {
          elements.editScopeTitle.classList.remove('active');
        }
        elements.drawingTitle.classList.add('active');
        break;
    }
  }
}

class MspSmallScreen extends MspScreen {
  constructor(config, editor) {
    super(config, editor);
  }

  doOnEditScopeClicked(oldScope) {
    ///* As long as the edit scope buttons come after the gallery items we want clicks on edit scope
    // * to update the drawing: */
    //this.editor.triggerDrawingClick();
  }

  doOnGalleryItemClicked() {
    /* This is the small screen on which we do NOT want to auto-update the drawing upon gallery item
     * clicks. The drawing should be updated upon click on edit scope or in the drawing itself.
     * Except, of course, if the edit scope slide is not needed. */
    //if (!this.isSlideNeeded(this.editor.editScopeSlide)) {
    //  this.editor.triggerDrawingClick();
    //}
  }

  stepActivated(step) {
    super.stepActivated(step);
    const elements = this.elements;
    /* On the small screen we risk the text wrapping after adding the active class. This causes a
     * little jump/flicker. Hence, also add the little class to allow us to avoid this by using the
     * proper css. */
    const classes = ['active', 'little'];
    switch (step) {
      case EditorSteps.gallery:
        elements.drawingTitle.classList.remove(...classes);
        break;
      case EditorSteps.editScope:
        elements.drawingTitle.classList.remove(...classes);
        break;
      case EditorSteps.drawing:
        elements.drawingTitle.classList.add(...classes);
        break;
    }
  }
}

const EditorSteps = {
  gallery: 0,
  editScope: 1,
  drawing: 2,
};

export class MultiStepProductEditor extends ProductDrawingViewModel {
  constructor(drawing, popupEditorConfig, defaultComponentType, productInteractionClass, name) {
    super(drawing, name);
    this.popupEditorConfig = popupEditorConfig;
    // The class (type) of the product interaction object we'll create (later)
    this.productInteractionClass = productInteractionClass;

    // Don't enable interaction until we're ready
    //drawing.setIsInteractive(true);
    const byId = id => document.getElementById(id);
    const bySelect = selectors => document.querySelector(selectors);

    // TODO: All these element references should be assigned to the popupEditorConfig by the caller
    // instead of finding them here.

    this.allSlides = document.querySelectorAll('.vp-editor .slides .slide');
    this.compTypeSlide = byId('comp-type-slide');
    this.editScopeSlide = byId('edit-scope-slide');
    this.gallerySlide = byId('finishes-slide');
    this.drawingSlide = byId('drawing-slide');
    this.currentSlideIndex = -1;
    this.activeSlides = [];

    const largeScreenConfig = {
      slides: [this.drawingSlide],
      componentTypeBtnGroup: bySelect('.vp-editor aside .comp-type .slide-menu'),
      editScopeBtnGroup: bySelect('.vp-editor aside .edit-scope .slide-menu'),
      gallery: bySelect('.vp-editor aside .finish-gallery'),
      elements: popupEditorConfig.elements.popup.largeScreen,
      content: popupEditorConfig.content,
      $galleryFilterMenu: popupEditorConfig.elements.popup.largeScreen.$galleryFilterMenu,
    };
    this.largeScreen = new MspLargeScreen(largeScreenConfig, this);

    const smallScreenConfig = {
      slides: [this.gallerySlide, this.editScopeSlide, this.drawingSlide],
      componentTypeBtnGroup: bySelect('.vp-editor .inner-main .comp-type .slide-menu'),
      editScopeBtnGroup: bySelect('.vp-editor .inner-main .edit-scope .slide-menu'),
      gallery: bySelect('.vp-editor .inner-main .finish-gallery'),
      elements: popupEditorConfig.elements.popup.smallScreen,
      content: popupEditorConfig.content,
      $galleryFilterMenu: popupEditorConfig.elements.popup.smallScreen.$galleryFilterMenu,
    };
    this.smallScreen = new MspSmallScreen(smallScreenConfig, this);

    this.currentScreen = null;
    this.clearScope();
    this.setComponentType(defaultComponentType, true);
    this.selectedValue = null;

    this.owl = jQuery('.vp-editor .owl-carousel');
    if (this.owl.length > 0) {
      this.owl.owlCarousel({
        items: 1,
        startPosition: 0,
        nav: false,
        dots: true,
        dotsContainer: '#slide-dots',
        //autoWidth: true,
      });
      this.owl.on('changed.owl.carousel', event => {
        //console.log('owl event....');
        //console.log(event.property);
        if (event.property && event.property.name === 'position') {
          const oldIndex = this.currentSlideIndex;
          this.currentSlideIndex = event.property.value;
          this.currentSlideChanged(oldIndex);
        }
      });
    }

    this.labelBtn = this.popupEditorConfig.elements.popup.labelsToggleBtn;
    this.labelBtn.onclick = (event) => {
      this.toggleFinishLabelBtn(this.labelBtn, (isActive) => {
        this.drawing.toggleFinishLabels(isActive);
      });
    };
    this.displayFinishLabelBtn(this.labelBtn, false);

    this.dimsBtn = this.popupEditorConfig.elements.popup.dimsToggleBtn;
    this.dimsBtn.onclick = (event) => {
      this.toggleFinishLabelBtn(this.dimsBtn, (isActive) => {
        // TODO: this.drawing.toggleDrawingDimensions(isActive);
      });
    };
    this.displayFinishLabelBtn(this.dimsBtn, false);
  }

  isFinishLabelBtnActive(button) {
    return button.classList.contains('on');
  }

  displayFinishLabelBtn(button, isActive) {
    if (isActive) {
      button.classList.remove('btn-secondary');
      button.classList.add('btn-success');
    } else {
      button.classList.add('btn-secondary');
      button.classList.remove('btn-success');
    }
  }

  toggleFinishLabelBtn(button, callback) {
    const wasActive = this.isFinishLabelBtnActive(button);
    button.classList.toggle('on');
    const isActive = !wasActive;
    this.displayFinishLabelBtn(button, isActive);
    if (callback) {
      callback(isActive);
    }
  }

  isDrawingVisible() {
    // TODO: Add the correct logic for detecting the drawing visibility here
    //const activeTabLink = document.querySelector('#showcase-tabs a.active[data-toggle="tab"]');
    //return ObjectUtils.isAssigned(activeTabLink) && activeTabLink.id === this.previewTabLinkId;
    return true;
  }

  startScreenResizeListener() {
    /* Note, we have multiple styles and code which must use matching media queries, see
     * _vpe-modal.scss, _vpe-editor.scss, MultiStepProductEditor.js, and DoorFrontEditor.cshtml.
     * Search for "large-screen-query"! */
    const largeScreenQuery = '(min-width: 768px)';// and (min-height: 700px)';
    this.largeScreenMediaQuerylist = window.matchMedia(largeScreenQuery);
    // Add listener:
    this.mediaQueryListListener = (e) => {
      this.updateCurrentScreenAndDisplaySlides(/*isLarge:*/ e.matches);
      this.refreshCurrentScreen();
    };
    this.largeScreenMediaQuerylist.addListener(this.mediaQueryListListener);
  }

  stopScreenResizeListener() {
    this.largeScreenMediaQuerylist.removeListener(this.mediaQueryListListener);
  }

  isDrawingSlideVisible() {
    return this.currentScreen.isDrawingSlideVisible();
  }

  setupSlideNavigation() {
    const owlTrigger = (event, callback) => {
      // Kind of a hack, but ignore any clicks on 'zoom-target':
      if (event.target.classList && event.target.classList.contains('zoom-target')) {
        return;
      }
      callback();
      event.preventDefault();
    };

    const prevSlide = event => owlTrigger(event, () => this.prevSlide());
    const nextSlide = event => owlTrigger(event, () => this.nextSlide());
    const goToSlide = (event, target) => owlTrigger(event, () => this.goToSlide(target));

    /* What this does is look for the 'data-target', 'data-slide', and 'data-slide-to' attributes
     * on any element underneath the .vp-editor element, and set up click handlers as appropriate. */
    const owlTargets = document.querySelectorAll('[data-target=".vp-editor .carousel"]');
    if (owlTargets) {
      owlTargets.forEach((item) => {
        switch (item.dataset.slide) {
          case 'prev':
            item.onclick = event => prevSlide(event);
            break;
          case 'next':
            item.onclick = event => nextSlide(event);
            break;
          default: {
            const target = item.dataset.slideTo;
            if (target) {
              item.onclick = event => goToSlide(event, target);
            }
          }
            break;
        }
      });
    }
  }

  setEditScope(editScope, forceIt) {
    if (typeof editScope === 'undefined') {
      editScope = null;
    }
    if (forceIt || editScope != this.editScope) {
      this.editScope = editScope;
      if (this.currentScreen) {
        this.currentScreen.editScopeChanged(editScope);
      }
      this.updateInteractionProperties();
      if (this.editScopeChanged) {
        this.editScopeChanged(this);
      }
    }
  }

  clearScope() {
    this.setEditScope(null, true);
  }

  setComponentType(componentType, forceIt) {
    if (forceIt || componentType != this.componentType) {
      this.componentType = componentType;
      if (this.currentScreen) {
        this.currentScreen.componentTypeChanged(componentType);
      }
      // Reset scope after component type changes
      this.setEditScope(this.componentType.defaultEditScope, true);
      this.updateInteractionProperties();
      if (this.componentTypeChanged) {
        this.componentTypeChanged(this);
      }
    }
  }

  setComponentTypeById(id) {
    this.setComponentType(ComponentType[id]);
  }

  setSelectedValue(value) {
    if (value != this.selectedValue) {
      this.selectedValue = value;
      this.currentScreen.selectedValueChanged(value);
      this.updateInteractionProperties();
      if (this.selectedValueChanged) {
        this.selectedValueChanged(this);
      }
    }
  }

  updateInteractionProperties() {
    const interaction = this.drawing.productInteraction;
    if (interaction) {
      interaction.componentType = this.componentType;
      interaction.editScope = this.editScope;
      interaction.selectedValue = this.selectedValue;
      this.toggleInteraction();
    }
  }

  triggerDrawingClick() {
    const interaction = this.drawing.productInteraction;
    if (interaction && interaction.triggerDrawingClick) {
      interaction.triggerDrawingClick();
    }
  }

  toggleInteraction() {
    // !! forces the expression to return a bool
    const on = !!(this.componentType && this.editScope && this.selectedValue);
    this.drawing.setIsInteractive(on);
  }

  applyGalleryFilter(filterKey, filterTitle) {
    this.componentType.filterKey = filterKey;
    this.componentType.filterTitle = filterTitle;
    this.currentScreen.applyGalleryFilter();
  }

  updateDrawingToolbar() {
    const isDrawingVisible = this.isDrawingSlideVisible();
    // Only show toggle buttons if drawing slide is visible:
    this.labelBtn.classList.toggle('d-none', !isDrawingVisible);
    // TODO: dimsBtn is not functional yet and is never displayed:
    this.dimsBtn.classList.toggle('d-none', true /*!isDrawingVisible*/);
  }

  //#region Slide Navigation

  currentSlideChanged(oldIndex) {
    this.backBtn.classList.toggle('d-none', this.currentSlideIndex === 0);
    this.updateDrawingToolbar();
    this.currentScreen.currentSlideChanged(oldIndex);
  }

  prevSlide() {
    this.owl.trigger('prev.owl.carousel');
  }

  nextSlide() {
    this.owl.trigger('next.owl.carousel');
  }

  goToSlide(slideIndex) {
    if (slideIndex > -1 && slideIndex < this.activeSlides.length &&
      slideIndex != this.currentSlideIndex) {
      this.owl.trigger('to.owl.carousel', [slideIndex]);
    }
  }

  addSlideAt(slideIndex, slideHtml) {
    return this.owl.trigger('add.owl.carousel', [slideHtml, slideIndex]);
  }

  removeSlideAt(slideIndex) {
    return this.owl.trigger('remove.owl.carousel', [slideIndex]);
  }

  refreshSlides() {
    return this.owl.trigger('refresh.owl.carousel');
  }

  getCurrentSlide() {
    return this.activeSlides ? this.activeSlides[this.currentSlideIndex] : null;
  }

  //#endregion

  /**
   * Is called when (before) a product is displayed, and whenever the screen is resized sufficiently.
   */
  updateCurrentScreenAndDisplaySlides(isLarge) {
    /* Note, which slides are needed depends on screen size, component type, and possibly other
     * properties. Study the isSlideNeeded() method for details. */

    const currentSlideIndex = this.currentSlideIndex;

    // Uninstall jQuery/Bootstrap event handlers before owl starts changing the DOM:
    if (this.currentScreen) {
      this.currentScreen.uninstallEventHandlers();
    }

    // First remove all current slides:
    this.activeSlides = [];
    const slideElems = document.querySelector('.vp-editor .owl-stage');
    let count = slideElems != null ? slideElems.children.length : 0;
    while (count > 0) {
      this.removeSlideAt(0);
      count--;
    }

    // Second, update current screen:
    this.currentScreen = isLarge ? this.largeScreen : this.smallScreen;

    // Third, display the new slides
    const slides = this.currentScreen.slides;
    // We need to add them to owl in reverse order:
    for (var i = slides.length - 1; i > -1; i--) {
      if (this.currentScreen.isSlideNeeded(slides[i])) {
        this.addSlideAt(0, slides[i]);
        this.activeSlides.push(slides[i]);
      }
    }
    // ...but activeSlides need to be in the original order:
    this.activeSlides.reverse();

    this.currentSlideIndex = 0; // Because owl just did the same when we added the slides above

    // Restore the current slide:
    this.goToSlide(currentSlideIndex);
    // Add click event handlers:
    this.setupSlideNavigation();
    // Reinstall jQuery/Bootstrap event handlers (after owl is done manipulating the DOM):
    this.currentScreen.installEventHandlers();
    // Trigger owl's refresh routine:
    this.refreshSlides();
  }

  /**
   * Is called when (after) a product is displayed, and whenever the screen is resized sufficiently.
   */
  refreshCurrentScreen(nextStep, initializeEditScope) {
    if (nextStep || nextStep === 0) {
      this.setCurrentStep(nextStep);
    } else {
      // Ensure the new screen is initialized:
      this.currentScreen.stepActivated(this.currentStep);
    }
    this.currentScreen.queryGalleryItems();

    if (initializeEditScope) {
      this.setEditScope(this.componentType.defaultEditScope, true);
    } else {
      this.currentScreen.editScopeChanged(this.editScope);
    }

    this.currentScreen.componentTypeChanged(this.componentType);
    // If we have a selected value, then ensure the current screen is updated with it:
    if (this.selectedValue) {
      this.currentScreen.selectedValueChanged(this.selectedValue);
    }
    this.currentScreen.scrollToActiveGalleryItem();
    this.currentScreen.updateDrawingTitle();
    this.updateDrawingToolbar();
  }

  setCurrentStep(step) {
    this.currentStep = step;
    this.currentScreen.stepActivated(step);
  }

  getValueFromProduct(apiProduct) {
    return this.componentType ? this.componentType.getValue(apiProduct) : null;
  }

  doBeforeDisplayProduct(data) {
    super.doBeforeDisplayProduct(data);
    const oldIx = this.drawing.productInteraction;
    // Clear event handlers from the old interaction:
    if (oldIx) {
      /*
      oldIx.onPanelFinishChanged = null;
      oldIx.onFrameFinishChanged = null;
      oldIx.onTrackFinishChanged = null;
      oldIx.onDoorLayoutChanged = null;
      */
      oldIx.onProductChanged = null;
    }
    // Remove the old interaction object from the drawing
    this.drawing.removeProductInteraction();

    // Setup media/screen size listener:
    this.startScreenResizeListener();
    /* We need to set up the slides (at least the slide with the drawing canvas), before calling
     * super.doStart() to ensure the drawing is created. */
    this.updateCurrentScreenAndDisplaySlides(this.largeScreenMediaQuerylist.matches);
  }

  /**
   * This class is an editor and the base class is a "displayer". Whenever the user wants to start
   * editing a product, the process is started by calling displayProduct(). We need here to think of
   * that as "editProduct()". Hence, in this method we must do everything which must be done every
   * time the user starts the editing.
   */
  doAfterDisplayProduct(data) {
    super.doAfterDisplayProduct(data);
    //const interaction = new DoorFrontInteraction();
    const interaction = new this.productInteractionClass();
    interaction.toggleInteractive = (displayObject, on, clickHandler) =>
      this.drawing.engine.toggleDisplayObjectInteractive(displayObject, on, clickHandler);
    /* Assign event handlers demonstrating how we can be notified when a component changes:
    interaction.onPanelFinishChanged = (interaction, oldFinish, changedComponent) => {
      console.log(`Changed the panel's value to ${changedComponent.finish}`);
      this.okBtn.removeAttribute('disabled');
    };
    interaction.onFrameFinishChanged = (interaction, oldFinish, changedComponent) => {
      console.log(`Changed the door's frame value to ${changedComponent.finish}`);
      this.okBtn.removeAttribute('disabled');
    };
    interaction.onTrackFinishChanged = (interaction, oldFinish, changedComponent) => {
      console.log(`Changed the front's track value to ${changedComponent.finish}`);
      this.okBtn.removeAttribute('disabled');
    };
    interaction.onDoorLayoutChanged = (interaction, oldLayout, changedComponent) => {
      console.log(`Changed the door's layout to ${changedComponent.panelLayout}`);
      this.okBtn.removeAttribute('disabled');
    };
    */

    // Is called once after one or more of the product's components changed
    interaction.onProductChanged = (interaction, componentType, changedProduct) => {
      //console.log(`Changed product ${changedProduct.name} of type ${componentType.id}`);
      this.okBtn.classList.remove('d-none');
      this.currentScreen.updateInUseGalleryItems();
    };

    // Install the new interaction
    this.drawing.addProductInteraction(interaction);
    /* 1/27/20: We don't want to pre-select anything in the gallery because we want the user to
     * reselect the value each time the editor is activated. Hence, don't call the setter, just
     * set the value directly here: */
    //this.setSelectedValue(this.getValueFromProduct(this.apiProduct));
    this.selectedValue = null;

    // Initialize current screen:
    this.refreshCurrentScreen(EditorSteps.gallery, true);

    // Assign values from the UI to the interaction:
    this.updateInteractionProperties();
    //this.currentScreen.activate();
    //this.setCurrentStep(EditorSteps.gallery);

    this.okBtn = data.elements.popup.okBtn;
    this.okBtn.classList.add('d-none');
    this.backBtn = data.elements.popup.backBtn;
    this.backBtn.classList.add('d-none');

    this.goToSlide(this.currentScreen.defaultSlideIndex);

    if (this.isFinishLabelBtnActive(this.labelBtn)) {
      this.drawing.toggleFinishLabels(true);
    }
    if (this.isFinishLabelBtnActive(this.dimsBtn)) {
      // TODO: this.drawing.toggleDrawingDimensions(isActive);
    }
  }

  doAfterHidingCurrentProduct(data) {
    super.doAfterHidingCurrentProduct(data);
    this.stopScreenResizeListener();
    this.drawing.setIsInteractive(false);
  }

  /** Asynchronously loads ALL the materials we expect will be needed in the app for the current
   * product type. */
  async loadProductMaterials() {
    return await super.loadProductMaterials();

    // TODO: Load the materials we know we'll present in the product editor (look at componentType, etc.).

    //const factory = ProductViewModelFactories.getFactory(this.apiProduct.type);
    //const materialIds = factory.getAllKnownProductMaterialIds(this.apiProduct);
    //console.log('Awaiting additional materials to be loaded...');
    //return await this.drawing.loadMaterials(materialIds, /*high res as well?*/ false);
    //console.log('Additional materials load completed!');
  }

  /**
   * Start (doStart) is only called once, typically the first time the user starts the product
   * editing process. The base class will implicitly call displayProduct so there's no need for the
   * user to call both.
   */
  doStart(apiProduct, data) {
    // This will call displayProduct():
    super.doStart(apiProduct, data);
  }
}
